import { initialize } from 'redux-form';
import { formatUrl } from 'url-lib';
import isEmpty from 'lodash/isEmpty';

import { getResource } from '@eb/resource-cache';
import { formatDate } from '@eb/datetime';
import { setWindowLocation } from '@eb/http';
import { eventCache } from '@eb/event-create-resource-cache';

import { getTimeForTimePicker } from './utils';
import { SCHEDULE_PUBLISH_DATE, STATE_RESOURCE_CACHE_NAME } from '../constants';
import { getPublishSettingsCacheKey } from './api';
import {
    DISPLAY_SALES_STATUS_CANCELLED,
    DISPLAY_SALES_STATUS_POSTPONED,
    LOADING_EVENT_SALES_STATUS_FORM,
    LOAD_EVENT_SALES_STATUS_COMPLETE,
} from '../constants/header';

import {
    EVENT_UPDATED_NOTIFICATION_URL_PARAM_NAME,
    EVENT_UPDATED_NOTIFICATION_URL_PARAM_SHOW_VALUE,
} from '../constants/notifications';

export const EVENT_PUBLISH_START = 'event-publish-start';
export const EVENT_PUBLISH_COMPLETE = 'event-publish-complete';
export const EVENT_UNPUBLISH_START = 'event-unpublish-start';
export const EVENT_UNPUBLISH_COMPLETE = 'event-unpublish-complete';
export const EVENT_CANCEL_START = 'event-cancel-start';
export const EVENT_CANCEL_COMPLETE = 'event-cancel-complete';
export const EVENT_DELETE_START = 'event-delete-start';
export const EVENT_DELETE_COMPLETE = 'event-delete-complete';
export const CLEAR_EVENT_STATUS_ERROR = 'clear-event-status-error';
export const SCHEDULE_PRIVACY_FAILURE = 'schedule-privacy-failure';
export const PUBLISH_TRANSITIONAL_MOMENT_START =
    'publish-transitional-moment-start';
export const PUBLISH_TRANSITIONAL_MOMENT_END =
    'publish-transitional-moment-end';
export const CANCEL_SCHEDULED_PUBLISH_COMPLETE =
    'cancel-scheduled-publish-complete';
export const SHOW_SCHEDULE_PUBLISH_NOTIFICATION =
    'create/notification/show-schedule-publish-notification';
export const HIDE_SCHEDULE_PUBLISH_NOTIFICATION =
    'create/notification/hide-schedule-publish-notification';
export const SCHEDULED_PUBLISH_COMPLETE = 'scheduled-publish-complete';
export const SAVE_SALES_STATUS_START = 'save-sales-status-start';
export const SAVE_SALES_STATUS_COMPLETE = 'save-sales-status-complete';

export const clearEventStatusError = () => ({
    type: CLEAR_EVENT_STATUS_ERROR,
});

/**
 * Action creators for transitional moment
 */
export const publishTransitionalMomentStart = () => ({
    type: PUBLISH_TRANSITIONAL_MOMENT_START,
});

export const publishTransitionalMomentEnd = () => ({
    type: PUBLISH_TRANSITIONAL_MOMENT_END,
});

/**
 * Action creators for publish
 */
export const eventPublishStart = (eventId) => ({
    type: EVENT_PUBLISH_START,

    payload: {
        eventId,
    },
});

export const eventPublishSuccess = (event, response) => ({
    type: EVENT_PUBLISH_COMPLETE,

    payload: {
        event,
        response,
    },
});

export const eventPublishFailure = (error, detail) => ({
    type: EVENT_PUBLISH_COMPLETE,

    payload: {
        error,
        detail,
    },

    error: true,
});

/**
 * Action creators for unpublish
 */
export const eventUnpublishStart = (eventId) => ({
    type: EVENT_UNPUBLISH_START,

    payload: {
        eventId,
    },
});

export const eventUnpublishSuccess = (event, response) => ({
    type: EVENT_UNPUBLISH_COMPLETE,

    payload: {
        event,
        response,
    },
});

export const eventUnpublishFailure = (error, detail) => ({
    type: EVENT_UNPUBLISH_COMPLETE,

    payload: {
        error,
        detail,
    },

    error: true,
});

/**
 * Action creators for unpublish
 */
export const eventCancelStart = (eventId) => ({
    type: EVENT_CANCEL_START,

    payload: {
        eventId,
    },
});

export const eventCancelSuccess = (event, response) => ({
    type: EVENT_CANCEL_COMPLETE,

    payload: {
        event,
        response,
    },
});

export const eventCancelFailure = (error, detail) => ({
    type: EVENT_CANCEL_COMPLETE,

    payload: {
        error,
        detail,
    },

    error: true,
});

/**
 * Action creators for delete
 */
export const eventDeleteStart = (eventId) => ({
    type: EVENT_DELETE_START,

    payload: {
        eventId,
    },
});

export const eventDeleteSuccess = (event, response) => ({
    type: EVENT_DELETE_COMPLETE,

    payload: {
        event,
        response,
    },
});

export const eventDeleteFailure = (error, detail) => ({
    type: EVENT_DELETE_COMPLETE,

    payload: {
        error,
        detail,
    },

    error: true,
});

/**
 * Action creators for scheduled publish
 */
export const cancelSchedulePublishFailure = (error) => ({
    type: CANCEL_SCHEDULED_PUBLISH_COMPLETE,

    payload: {
        error,
    },

    error: true,
});

export const schedulePublishFailure = (error) => ({
    type: SCHEDULED_PUBLISH_COMPLETE,

    payload: {
        error,
    },

    error: true,
});

export const schedulePrivacySettingsFailure = (error) => ({
    type: SCHEDULE_PRIVACY_FAILURE,

    payload: {
        error,
    },

    error: true,
});

/**
 * Action creators for sales status
 */
export const saveSalesStatusStart = (eventId) => ({
    type: SAVE_SALES_STATUS_START,

    payload: {
        eventId,
    },
});

export const saveSalesStatusSuccess = (event, response) => ({
    type: SAVE_SALES_STATUS_COMPLETE,

    payload: {
        event,
        response,
    },
});

export const saveSalesStatusFailure = (error) => ({
    type: SAVE_SALES_STATUS_COMPLETE,

    payload: {
        error,
    },

    error: true,
});

export const endOnlineSalesFailure = (error) => ({
    type: SAVE_SALES_STATUS_COMPLETE,

    payload: {
        error,
    },

    error: true,
});

export const loadingEventSalesStatusModal = () => ({
    type: LOADING_EVENT_SALES_STATUS_FORM,
});

export const loadEventSalesStatusComplete = () => ({
    type: LOAD_EVENT_SALES_STATUS_COMPLETE,
});

export const navigateAfterClick = (eventId, eventStatus) => (
    dispatch,
    getState,
) => {
    const ticketsUrl = formatUrl(`/manage/events/${eventId}/tickets/`, {
        [EVENT_UPDATED_NOTIFICATION_URL_PARAM_NAME]: EVENT_UPDATED_NOTIFICATION_URL_PARAM_SHOW_VALUE,
    });

    const eventStatusMap = {
        [DISPLAY_SALES_STATUS_CANCELLED]: 'ce',
        [DISPLAY_SALES_STATUS_POSTPONED]: 'pe',
    };

    const emailAttendeesUrl = formatUrl('/attendees-email', {
        eid: eventId,
        action: 'CREATE',
        flow: eventStatusMap[eventStatus],
    });
    const shouldRedirectToAttendeeEmail =
        eventStatus === DISPLAY_SALES_STATUS_CANCELLED ||
        eventStatus === DISPLAY_SALES_STATUS_POSTPONED;

    shouldRedirectToAttendeeEmail
        ? setWindowLocation(emailAttendeesUrl)
        : setWindowLocation(ticketsUrl);
};

export const initializeSchedulePublishFormData = (formName, initFormData) => (
    dispatch,
) => {
    dispatch(initialize(formName, initFormData));
};

export const showSchedulePublishNotification = (status = true) => ({
    type: status
        ? SHOW_SCHEDULE_PUBLISH_NOTIFICATION
        : HIDE_SCHEDULE_PUBLISH_NOTIFICATION,
});

export const populateSchedulePublish = (state, eventId) => {
    const cache = state[STATE_RESOURCE_CACHE_NAME];
    const { start } = eventCache.getResource(state, { eventId }) || {};
    const publishSettings =
        getResource(cache, getPublishSettingsCacheKey(eventId)) || {};
    let eventStartDate;
    let eventStartTime;
    let eventTimezone;
    const defaultSchedulePublishDateTime =
        publishSettings[SCHEDULE_PUBLISH_DATE];
    if (!isEmpty(start)) {
        const eventStartDateLocal = start.local;

        eventStartDate = formatDate(eventStartDateLocal);
        eventStartTime = getTimeForTimePicker(eventStartDateLocal);
        eventTimezone = start.timezone;
    }
    return {
        eventStartDate,
        eventStartTime,
        eventTimezone,
        defaultSchedulePublishDateTime,
        eventId,
    };
};
